import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/api';
import './DataAvailability.css';

interface DataAvailabilityProps {
  broker: string;
  symbol: string;
  onDataLoaded?: () => void;
}

interface AvailableRange {
  from: string;
  to: string;
  candles: number;
}

interface DownloadJobStatus {
  job_id: string;
  status: string;
  progress: number;
  completed_symbols: number;
  total_symbols: number;
  error_message?: string;
}

const DataAvailability: React.FC<DataAvailabilityProps> = ({ broker, symbol, onDataLoaded }) => {
  const [availability, setAvailability] = useState<{ [key: string]: AvailableRange } | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (broker && symbol) {
      checkAvailability();
    }
  }, [broker, symbol]);

  useEffect(() => {
    if (jobId && isDownloading) {
      const interval = setInterval(() => {
        checkJobStatus(jobId);
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [jobId, isDownloading]);

  const checkAvailability = async () => {
    try {
      const response = await fetch(
        getApiUrl(`/market-data/available-range?broker=${broker}&symbol=${symbol}`),
        { credentials: 'include' }
      );
      const data = await response.json();

      if (data.intervals && Object.keys(data.intervals).length > 0) {
        setAvailability(data.intervals);
      } else {
        setAvailability(null);
      }
    } catch (err) {
      console.error('Failed to check availability:', err);
      setAvailability(null);
    }
  };

  const startDownload = async () => {
    setError('');
    setIsDownloading(true);
    setDownloadProgress(0);

    const currentYear = new Date().getFullYear();
    const fromDate = `${currentYear - 2}-01-01`; // Last 2 years
    const toDate = `${currentYear}-12-31`;

    try {
      // Get API token from futures cache (no broker-connector dependency)
      let apiToken = null;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('futures_cache_')) {
          const cached = localStorage.getItem(key);
          if (cached) {
            try {
              const cache = JSON.parse(cached);
              if (cache.broker === broker && cache.apiToken) {
                apiToken = cache.apiToken;
                break;
              }
            } catch (e) {
              continue;
            }
          }
        }
      }

      if (!apiToken) {
        throw new Error('Broker not connected. Please connect broker in Trading page first');
      }

      // Start download with token
      const response = await fetch(getApiUrl('/market-data/download'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          broker,
          symbols: [symbol],
          intervals: ['1m', '1h', '1d'],
          from_date: fromDate,
          to_date: toDate,
          api_token: apiToken,
        }),
      });

      const data = await response.json();
      if (data.job_id) {
        setJobId(data.job_id);
      } else {
        throw new Error('No job ID returned');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start download');
      setIsDownloading(false);
    }
  };

  const checkJobStatus = async (jobId: string) => {
    try {
      const response = await fetch(getApiUrl(`/market-data/download/status/${jobId}`), { credentials: 'include' });
      const job: DownloadJobStatus = await response.json();

      setDownloadProgress(job.progress);

      if (job.status === 'completed') {
        setIsDownloading(false);
        setDownloadProgress(100);
        checkAvailability(); // Refresh availability
        if (onDataLoaded) {
          onDataLoaded();
        }
      } else if (job.status === 'failed') {
        setIsDownloading(false);
        setJobId(null); // Clear job ID to allow retry
        setError(job.error_message || 'Download failed. Please try again.');
      }
    } catch (err) {
      console.error('Failed to check job status:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ru-RU');
  };

  if (!broker || !symbol) {
    return null;
  }

  return (
    <div className="data-availability">
      <h3>📊 Исторические данные</h3>

      {availability ? (
        <div className="availability-info">
          <div className="availability-status success">
            <span className="status-icon">✅</span>
            <span>Данные доступны</span>
          </div>

          <div className="intervals-list">
            {Object.entries(availability).map(([interval, range]) => (
              <div key={interval} className="interval-item">
                <span className="interval-badge">{interval}</span>
                <span className="interval-range">
                  {formatDate(range.from)} - {formatDate(range.to)}
                </span>
                <span className="interval-count">
                  {range.candles.toLocaleString()} свечей
                </span>
              </div>
            ))}
          </div>

          <button className="btn-refresh" onClick={checkAvailability}>
            🔄 Обновить данные
          </button>
        </div>
      ) : (
        <div className="availability-info">
          <div className="availability-status warning">
            <span className="status-icon">⚠️</span>
            <span>Данные не найдены</span>
          </div>

          {!isDownloading ? (
            <button className="btn-download primary" onClick={startDownload}>
              📥 Загрузить исторические данные
            </button>
          ) : (
            <div className="download-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${downloadProgress}%` }}
                />
              </div>
              <div className="progress-text">
                Загрузка... {downloadProgress}%
              </div>
            </div>
          )}

          {error && (
            <>
              <div className="error-message">
                ❌ {error}
              </div>
              <button className="btn-download primary" onClick={startDownload}>
                🔄 Попробовать снова
              </button>
            </>
          )}

          <p className="info-text">
            Будут загружены данные за последние 2 года в таймфреймах: 1m, 1h, 1d
          </p>
        </div>
      )}
    </div>
  );
};

export default DataAvailability;
